/**
 * A firm's sign-in policy, client side (enterprise batch 10).
 *
 * The backend refuses every request with 401 and one of these codes when the
 * sign-in does not meet the firm's rules (uw_backend/app/auth/auth0.py,
 * check_sign_in_policy):
 *
 *   mfa_required  -> sign in again asking Auth0 for a second factor
 *                    (acr_values, honoured by the Post-Login Action)
 *   sso_required  -> sign in again through the firm's connection
 *
 * App.tsx calls `stepUpFor` when /check_user answers with either. A second
 * refusal within a few minutes means the step-up did not help (the Action is
 * not updated, or the person used the wrong login), so instead of looping
 * the person is signed out with an explanation.
 */

export const MFA_ACR = 'http://schemas.openid.net/pape/policies/2007/06/multi-factor';

const ATTEMPT_KEY = 'uw_signin_stepup';
const MFA_FIRM_KEY = 'uw_firm_requires_mfa';
const LOOP_WINDOW_MS = 3 * 60 * 1000;

export interface PolicyRefusal {
  code?: string;
  connection?: string;
  acr_values?: string;
  description?: string;
}

export function isPolicyRefusal(status: number, body: PolicyRefusal | null | undefined): boolean {
  return status === 401 && !!body && (body.code === 'mfa_required' || body.code === 'sso_required');
}

function read(key: string, store: Storage): string | null {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null, store: Storage): void {
  try {
    if (value === null) store.removeItem(key);
    else store.setItem(key, value);
  } catch {
    // Private browsing can refuse storage; the server still enforces the rule.
  }
}

/** Login parameters for the Log in button: ask for two-factor up front when
 *  this browser's last sign-in was into a firm that requires it. */
export function rememberedLoginParams(): Record<string, string> {
  return read(MFA_FIRM_KEY, localStorage) === '1' ? { acr_values: MFA_ACR } : {};
}

/** Remember whether this browser's firm requires two-factor (UserContext
 *  calls it once the firm has loaded). */
export function rememberFirmMfa(required: boolean): void {
  write(MFA_FIRM_KEY, required ? '1' : null, localStorage);
}

/** The sign-in worked: forget any step-up in progress. */
export function signInAccepted(): void {
  write(ATTEMPT_KEY, null, sessionStorage);
}

/** What to tell someone whose step-up did not satisfy the firm. */
export function refusalMessage(body: PolicyRefusal): string {
  if (body.code === 'sso_required') {
    return `Your firm requires signing in with your company account${body.connection ? ` (${body.connection})` : ''}. `
      + 'Use your company login, or ask your firm\'s admin for help.';
  }
  return 'Your firm requires two-factor sign-in, and this sign-in did not complete it. '
    + 'Log in again and finish the second step, or ask your firm\'s admin for help.';
}

/**
 * Decide the next move for a policy refusal: `{ redirect: params }` to sign
 * in again with those Auth0 parameters, or `{ message }` when a step-up was
 * already tried moments ago and the person should be signed out instead.
 */
export function stepUpFor(body: PolicyRefusal, email?: string | null):
  { redirect: Record<string, string> } | { message: string } {
  const now = Date.now();
  const last = read(ATTEMPT_KEY, sessionStorage);
  if (last) {
    const [code, at] = last.split('|');
    if (code === body.code && now - Number(at) < LOOP_WINDOW_MS) {
      write(ATTEMPT_KEY, null, sessionStorage);
      return { message: refusalMessage(body) };
    }
  }
  write(ATTEMPT_KEY, `${body.code}|${now}`, sessionStorage);
  if (body.code === 'mfa_required') {
    write(MFA_FIRM_KEY, '1', localStorage);
    // No prompt=login: the Auth0 session already knows who this is, so the
    // person only sees the second-factor step.
    return { redirect: { acr_values: body.acr_values || MFA_ACR } };
  }
  const params: Record<string, string> = { prompt: 'login' };
  if (body.connection) params.connection = body.connection;
  if (email) params.login_hint = email;
  return { redirect: params };
}
